// src/lib/ltvDecomposed.js — SOUND-810 (LTV v2: decomposed model)
//
// PURE calculator for the decomposed LTV model. No React, no fetch. It reads
// the v2 cohort spine (GET /ltv/v2/cohorts, over MART_LTV_COHORT_V2) and
// returns net LTV at 3 / 6 / 12 months, split into measured and projected.
// It replaced the v1 modeler (lib/ltvModel.js, retired in SOUND-837).
//
// This is a port of the SOUND-808 comparison harness (sound-ltv repo,
// ltv_compare/models.py `curve('decomposed')`, `sbg_fit`, evaluate.py `vbar`,
// interpret.py `value_from_renewals`). Unit tests pin it to harness output.
//
// ─── The model (proposal, Section 8) ──────────────────────────────────────
//   renewal r_k = (1 − cancel share_k) × payment success_k
//     1 − cancel share_k = attempted_k / eligible_k
//     payment success_k  = renewed_k / attempted_k
//   Observed cycles use these measured ratios. Beyond the last observed cycle:
//     • cancel side: sBG-hybrid. A 2-parameter shifted-beta-geometric curve
//       fitted on cycles ≥ 3 (cycle 2, the first renewal, is always measured):
//         (1 − cancel)_k = (β + k − 3) / (α + β + k − 3)
//     • payment side: carried at its average over the last 3 known cycles.
//   Survival S_1 = 1, S_k = ∏_{j=2..k} r_j.
//   LTV_H = Σ_{k=1..K_H} S_k × v_k, where
//     v_k = average net value per subscription PAID at cycle k, and
//     K_H = 1 + floor(H × 30.4 / cadence days), capped at 30 cycles.
//
// ─── Pooling ─────────────────────────────────────────────────────────────
//   Mart counts already cover only subscriptions whose cycle outcome is known
//   (due date + 30-day retry buffer), so every row is pooled by SUMMING across
//   cohorts; there is no maturity filter. Never average per-cohort ratios.
//   A product with fewer than 200 first renewals (eligible at cycle 2) uses
//   its client's pooled curve; the result says so (usedClientCurve).
//
// ─── Cost inputs (decomposedEconomics) ──────────────────────────────────────
//   The page keeps its user-entered costs (proposal Section 3.2). They are
//   applied on top of the model, never inside it: per paying subscription per
//   cycle, var cost_k = ship + processor % x v_k + processor $/txn + COGS
//   (cycleEconomics below, carried over from the retired v1 modeler), and CAC once.
//     contribution LTV_H = Σ_{k≤K_H} S_k x (v_k − var cost_k)
//     break-even = the cycle where Σ S_k x (v_k − var cost_k) first reaches CAC
//   No discounting.


export const MAX_CYCLE = 30;
export const HORIZONS = [3, 6, 12];
// A product needs this many first renewals for its own curve (proposal 8.3).
export const MIN_FIRST_RENEWALS = 200;
// A cycle's value per payer needs this many paying subscriptions.
export const MIN_PAYERS_FOR_VALUE = 30;
// "No trace" jump flag (gap N2): a cohort's never-attempted share at the first
// renewal is at least double, and 5 points above, its last 3 cohorts' share.
export const NO_TRACE_RATIO = 2;
export const NO_TRACE_MIN_LIFT = 0.05;
export const NO_TRACE_MIN_ELIGIBLE = 50;
const DAYS_PER_MONTH = 30.4;
const DEFAULT_CADENCE_DAYS = 30;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Per-order variable cost and per-paying-subscription contribution. */
function cycleEconomics(revPerCust, a) {
  const varCost = num(a.ship) + num(a.proc_pct) * revPerCust + num(a.proc_txn) + num(a.cogs);
  return { revPerCust, varCost, contribution: revPerCust - varCost };
}

/** Net value column for a revenue basis: "new" (charged − tax) or "orig". */
function valueColumns(basis) {
  return basis === "orig"
    ? { paid: "net_value_paid_orig", all: "net_value_orig" }
    : { paid: "net_value_paid", all: "net_value" };
}

// ─── Pooling ────────────────────────────────────────────────────────────────

/**
 * Sum mart rows into one entry per billing cycle.
 * @param {object[]} rows  mart_ltv_cohort_v2 rows (any mix of cohorts)
 * @param {"new"|"orig"} basis  revenue definition for the value columns
 * @returns {Map<number, object>} cycle → pooled counts and dollars
 */
export function poolCycles(rows, basis = "new") {
  const cols = valueColumns(basis);
  const byCycle = new Map();
  for (const row of rows || []) {
    const k = Number(row.billing_cycle);
    if (!Number.isFinite(k) || k < 1 || k > MAX_CYCLE) continue;
    let p = byCycle.get(k);
    if (!p) {
      p = {
        cycle: k, eligible: 0, attempted: 0, renewed: 0, paid: 0, valuePaid: 0,
        renewedFirstTry: 0, renewedAfterDecline: 0, lostPaymentFailed: 0, lostNoAttempt: 0,
        subRev: 0, refundAmt: 0, chargebackAmt: 0, addonRev: 0, upsellRev: 0,
        otherRefundAmt: 0, otherChargebackAmt: 0, netValue: 0, cohortSize: 0,
      };
      byCycle.set(k, p);
    }
    p.eligible += num(row.eligible);
    p.attempted += num(row.attempted);
    p.renewed += num(row.renewed);
    p.paid += num(row.paid);
    p.valuePaid += num(row[cols.paid]);
    p.renewedFirstTry += num(row.renewed_first_try);
    p.renewedAfterDecline += num(row.renewed_after_decline);
    p.lostPaymentFailed += num(row.lost_payment_failed);
    p.lostNoAttempt += num(row.lost_no_attempt);
    p.subRev += num(row.sub_rev);
    p.refundAmt += num(row.refund_amt);
    p.chargebackAmt += num(row.chargeback_amt);
    p.addonRev += num(row.addon_rev);
    p.upsellRev += num(row.upsell_rev);
    p.otherRefundAmt += num(row.other_refund_amt);
    p.otherChargebackAmt += num(row.other_chargeback_amt);
    p.netValue += num(row[cols.all]);
    if (k === 1) p.cohortSize += num(row.cohort_size);
  }
  return byCycle;
}

/**
 * The transition counts the curve is fitted on: cycles ≥ 2 with eligible > 0,
 * in cycle order (the harness's `counts()` table).
 */
export function transitionCounts(pooled) {
  return [...pooled.values()]
    .filter((p) => p.cycle >= 2 && p.eligible > 0)
    .sort((a, b) => a.cycle - b.cycle)
    .map(({ cycle, eligible, attempted, renewed }) => ({ cycle, eligible, attempted, renewed }));
}

// ─── sBG fit ────────────────────────────────────────────────────────────────

/**
 * Nelder–Mead minimiser, a line-for-line port of scipy.optimize's default
 * (non-adaptive) method, so the browser fit lands where the harness's does.
 * @param {(x: number[]) => number} f
 * @param {number[]} x0
 * @returns {{ x: number[], fun: number }}
 */
export function nelderMead(f, x0, { xatol = 1e-4, fatol = 1e-4 } = {}) {
  const n = x0.length;
  const maxIter = 200 * n;
  const maxFev = 200 * n;
  const [rho, chi, psi, sigma] = [1, 2, 0.5, 0.5];
  let fcalls = 0;
  const fn = (x) => { fcalls += 1; return f(x); };

  // Initial simplex: x0, plus each coordinate nudged by 5% (0.00025 if zero).
  let sim = [x0.slice()];
  for (let k = 0; k < n; k++) {
    const y = x0.slice();
    y[k] = y[k] !== 0 ? 1.05 * y[k] : 0.00025;
    sim.push(y);
  }
  let fsim = sim.map(fn);
  const order = () => {
    const idx = fsim.map((v, i) => i).sort((a, b) => fsim[a] - fsim[b] || a - b);
    sim = idx.map((i) => sim[i]);
    fsim = idx.map((i) => fsim[i]);
  };
  const comb = (a, xa, b, xb) => xa.map((v, i) => a * v + b * xb[i]);
  order();

  let iterations = 1;
  while (fcalls < maxFev && iterations < maxIter) {
    // Converged when every vertex is within xatol of the best, and every value
    // within fatol of the best value.
    let xSpread = 0;
    let fSpread = 0;
    for (let j = 1; j <= n; j++) {
      for (let i = 0; i < n; i++) xSpread = Math.max(xSpread, Math.abs(sim[j][i] - sim[0][i]));
      fSpread = Math.max(fSpread, Math.abs(fsim[0] - fsim[j]));
    }
    if (xSpread <= xatol && fSpread <= fatol) break;

    // Centroid of every vertex but the worst.
    const xbar = new Array(n).fill(0);
    for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) xbar[i] += sim[j][i] / n;
    const worst = sim[n];
    const xr = comb(1 + rho, xbar, -rho, worst);
    const fxr = fn(xr);
    let shrink = false;
    if (fxr < fsim[0]) {
      const xe = comb(1 + rho * chi, xbar, -rho * chi, worst);
      const fxe = fn(xe);
      if (fxe < fxr) { sim[n] = xe; fsim[n] = fxe; } else { sim[n] = xr; fsim[n] = fxr; }
    } else if (fxr < fsim[n - 1]) {
      sim[n] = xr; fsim[n] = fxr;
    } else if (fxr < fsim[n]) {
      const xc = comb(1 + psi * rho, xbar, -psi * rho, worst);
      const fxc = fn(xc);
      if (fxc <= fxr) { sim[n] = xc; fsim[n] = fxc; } else shrink = true;
    } else {
      const xcc = comb(1 - psi, xbar, psi, worst);
      const fxcc = fn(xcc);
      if (fxcc < fsim[n]) { sim[n] = xcc; fsim[n] = fxcc; } else shrink = true;
    }
    if (shrink) {
      // Pull every vertex halfway toward the best one.
      const best = sim[0];
      for (let j = 1; j <= n; j++) {
        const next = new Array(n);
        for (let i = 0; i < n; i++) next[i] = best[i] + sigma * (sim[j][i] - best[i]);
        sim[j] = next;
        fsim[j] = fn(next);
      }
    }
    iterations += 1;
    order();
  }
  return { x: sim[0], fun: fsim[0] };
}

const clip = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

/**
 * Maximum-likelihood sBG fit of r(n) = (β + n) / (α + β + n), n = 0, 1, ...
 * Fitted in log space from 4 starting points (as the harness does); the best
 * fit wins. Needs at least 2 points.
 * @param {number[]} E  trials (eligible) per point
 * @param {number[]} S  successes per point
 * @param {number[]} n  renewal index per point
 * @returns {{ alpha: number, beta: number } | null}
 */
export function fitSbg(E, S, n) {
  if (E.length < 2) return null;
  const nll = (th) => {
    const a = Math.exp(clip(th[0], -6, 8));
    const b = Math.exp(clip(th[1], -6, 8));
    let total = 0;
    for (let i = 0; i < E.length; i++) {
      const r = clip((b + n[i]) / (a + b + n[i]), 1e-9, 1 - 1e-9);
      total -= S[i] * Math.log(r) + (E[i] - S[i]) * Math.log(1 - r);
    }
    return total;
  };
  const starts = [[0, 0], [1, 1], [-1, 0], [2, 3]];
  let best = null;
  for (const x0 of starts) {
    const res = nelderMead(nll, x0);
    if (!best || res.fun < best.fun) best = res;
  }
  return { alpha: Math.exp(clip(best.x[0], -6, 8)), beta: Math.exp(clip(best.x[1], -6, 8)) };
}

// ─── Curve ──────────────────────────────────────────────────────────────────

/**
 * Decomposed renewal curve from pooled transition counts.
 * @param {{cycle, eligible, attempted, renewed}[]} counts  cycles ≥ 2, sorted
 * @returns {null | {
 *   renewal: number[],        // index k = renewal into cycle k (2..30); [0], [1] unused
 *   notCancel: number[], paySuccess: number[],
 *   lastObservedCycle: number, sbg: {alpha, beta} | null }}
 */
export function decomposedCurve(counts) {
  if (!counts.length) return null;
  const byCycle = new Map(counts.map((c) => [c.cycle, c]));
  const last = counts[counts.length - 1].cycle;
  const notCancelObs = (k) => (byCycle.has(k) ? byCycle.get(k).attempted / byCycle.get(k).eligible : NaN);
  const payObs = (k) => {
    const c = byCycle.get(k);
    return c && c.attempted > 0 ? c.renewed / c.attempted : NaN;
  };

  // Cancel side: sBG on cycles ≥ 3 (eligible → attempted).
  const tail = counts.filter((c) => c.cycle >= 3);
  const sbg = fitSbg(tail.map((c) => c.eligible), tail.map((c) => c.attempted), tail.map((c) => c.cycle - 3));

  // Payment side beyond the data: the last 3 known cycles, pooled.
  const recent = counts.slice(-3);
  const recentAttempted = recent.reduce((s, c) => s + c.attempted, 0);
  const payRecent = recent.reduce((s, c) => s + c.renewed, 0) / Math.max(recentAttempted, 1);

  const renewal = new Array(MAX_CYCLE + 1).fill(NaN);
  const notCancel = new Array(MAX_CYCLE + 1).fill(NaN);
  const paySuccess = new Array(MAX_CYCLE + 1).fill(NaN);
  for (let k = 2; k <= MAX_CYCLE; k++) {
    let v = notCancelObs(k);
    if (sbg && (Number.isNaN(v) || k > last)) v = (sbg.beta + k - 3) / (sbg.alpha + sbg.beta + k - 3);
    if (Number.isNaN(v)) v = notCancelObs(last);
    let d = k <= last ? payObs(k) : payRecent;
    if (Number.isNaN(d)) d = payRecent;
    notCancel[k] = v;
    paySuccess[k] = d;
    renewal[k] = v * d;
  }
  return { renewal, notCancel, paySuccess, lastObservedCycle: last, sbg };
}

/**
 * Average net value per paying subscription, by cycle (index 1..30).
 * Cycles with fewer than MIN_PAYERS_FOR_VALUE payers take the value of the
 * highest cycle that has enough; with none at all, 0.
 */
export function valuePerPayer(pooled) {
  const v = new Array(MAX_CYCLE + 1).fill(NaN);
  let lastValid = null;
  for (let k = 1; k <= MAX_CYCLE; k++) {
    const p = pooled.get(k);
    if (p && p.paid >= MIN_PAYERS_FOR_VALUE) { v[k] = p.valuePaid / p.paid; lastValid = v[k]; }
  }
  const fill = lastValid == null ? 0 : lastValid;
  return v.map((x, k) => (k === 0 ? NaN : Number.isNaN(x) ? fill : x));
}

/** Cycles inside an H-month horizon for a cadence: 1 + floor(H × 30.4 / cadence), 1..30. */
export function horizonCycles(months, cadenceDays) {
  const g = cadenceDays > 0 ? cadenceDays : DEFAULT_CADENCE_DAYS;
  return clip(1 + Math.floor((months * DAYS_PER_MONTH) / g), 1, MAX_CYCLE);
}

/** Cadence for a set of rows: the subscription-weighted median of cadence_days. */
export function cadenceOf(rows) {
  const pts = (rows || [])
    .filter((r) => Number(r.billing_cycle) === 1 && num(r.cadence_days) > 0)
    .map((r) => ({ g: num(r.cadence_days), w: Math.max(num(r.cohort_size), 1) }))
    .sort((a, b) => a.g - b.g);
  if (!pts.length) return DEFAULT_CADENCE_DAYS;
  const half = pts.reduce((s, p) => s + p.w, 0) / 2;
  let acc = 0;
  for (const p of pts) { acc += p.w; if (acc >= half) return p.g; }
  return pts[pts.length - 1].g;
}

/** Σ_{k=1..K} S_k v_k, split at the last observed cycle. */
function sumValue(renewal, value, K, lastObserved) {
  let S = 1;
  let measured = 0;
  let projected = 0;
  const survival = [];
  for (let k = 1; k <= K; k++) {
    if (k >= 2) S *= renewal[k];
    const pv = S * value[k];
    if (k <= lastObserved) measured += pv; else projected += pv;
    survival.push({ cycle: k, survival: S, value: value[k], contribution: pv, observed: k <= lastObserved });
  }
  return { ltv: measured + projected, measured, projected, survival };
}

// ─── Per cohort ─────────────────────────────────────────────────────────────

/**
 * Net LTV per cohort. A cohort's own actuals are used for every cycle where
 * all its subscriptions have a known outcome (is_complete); from there, the
 * subscriptions still paying are projected with the pooled curve.
 * This is a cohort-level version of the harness's per-subscription method.
 */
export function cohortBreakdown(rows, curve, value, cadenceDays, basis = "new") {
  const cols = valueColumns(basis);
  const cohorts = new Map();
  for (const r of rows || []) {
    const key = r.cohort_month;
    if (!cohorts.has(key)) cohorts.set(key, []);
    cohorts.get(key).push(r);
  }
  const months = [...cohorts.keys()].sort();
  const out = months.map((month) => {
    // Pool currency rows within the cohort (when currency = all).
    const byCycle = new Map();
    for (const r of cohorts.get(month)) {
      const k = Number(r.billing_cycle);
      const p = byCycle.get(k) || { size: 0, known: 0, paid: 0, value: 0, eligible: 0, renewed: 0, lostNoAttempt: 0, complete: true };
      p.size += num(r.cohort_size);
      p.known += num(r.subs_known);
      p.paid += num(r.paid);
      p.value += num(r[cols.all]);
      p.eligible += num(r.eligible);
      p.renewed += num(r.renewed);
      p.lostNoAttempt += num(r.lost_no_attempt);
      p.complete = p.complete && (r.is_complete === true || r.is_complete === "true");
      byCycle.set(k, p);
    }
    const size = byCycle.get(1)?.size || 0;
    // Last cycle where the whole cohort's outcome is known, counting up from 1.
    let lastComplete = 0;
    for (let k = 1; k <= MAX_CYCLE; k++) {
      const p = byCycle.get(k);
      if (p && p.complete && p.known === size) lastComplete = k; else break;
    }
    const first = byCycle.get(2);
    const horizons = {};
    for (const H of HORIZONS) {
      const K = horizonCycles(H, cadenceDays);
      let measured = 0;
      for (let k = 1; k <= Math.min(lastComplete, K); k++) measured += byCycle.get(k).value / Math.max(size, 1);
      let projected = 0;
      if (curve && K > lastComplete && lastComplete >= 1) {
        let S = byCycle.get(lastComplete).paid / Math.max(size, 1);
        for (let k = lastComplete + 1; k <= K; k++) { S *= curve.renewal[k]; projected += S * value[k]; }
      }
      horizons[H] = { ltv: measured + projected, measured, projected, K };
    }
    return {
      cohortMonth: month,
      size,
      lastCompleteCycle: lastComplete,
      // No first renewal observed for the whole cohort yet: the number leans
      // entirely on the pooled curve, so the page labels it "indicative".
      indicative: lastComplete < 2,
      firstRenewal: first && first.eligible > 0 ? first.renewed / first.eligible : null,
      firstRenewalEligible: first ? first.eligible : 0,
      noTraceShare: first && first.eligible > 0 ? first.lostNoAttempt / first.eligible : null,
      _first: first || null,
      horizons,
    };
  });

  // First-renewal check and "no trace" flag vs. the 3 previous cohorts with a
  // first renewal, pooled (counts summed, then divided).
  out.forEach((c, i) => {
    const prev = out.slice(0, i).filter((p) => p._first && p._first.eligible > 0).slice(-3);
    const elig = prev.reduce((s, p) => s + p._first.eligible, 0);
    c.baselineFirstRenewal = elig > 0 ? prev.reduce((s, p) => s + p._first.renewed, 0) / elig : null;
    c.baselineNoTraceShare = elig > 0 ? prev.reduce((s, p) => s + p._first.lostNoAttempt, 0) / elig : null;
    c.noTraceJump =
      c.noTraceShare != null && c.baselineNoTraceShare != null &&
      c.firstRenewalEligible >= NO_TRACE_MIN_ELIGIBLE &&
      c.noTraceShare >= NO_TRACE_RATIO * c.baselineNoTraceShare &&
      c.noTraceShare - c.baselineNoTraceShare >= NO_TRACE_MIN_LIFT;
  });
  return out.map(({ _first, ...c }) => c);
}

// ─── Entry point ────────────────────────────────────────────────────────────

/**
 * Decomposed net LTV for one product (or offer family) from the v2 spine.
 * @param {object[]} rows        mart_ltv_cohort_v2 rows for the product
 * @param {object}   [opts]
 * @param {object[]} [opts.clientRows]  the client's rows across all products
 *        (same currency), pooled for the curve when the product is thin
 * @param {"new"|"orig"} [opts.basis]   revenue definition (default "new")
 */
export function computeDecomposedLtv(rows, { clientRows = null, basis = "new" } = {}) {
  const warnings = [];
  const pooled = poolCycles(rows, basis);
  if (!pooled.size) return emptyDecomposed(warnings.concat("no spine data"));

  let counts = transitionCounts(pooled);
  const firstRenewals = counts.find((c) => c.cycle === 2)?.eligible || 0;
  let usedClientCurve = false;
  if (firstRenewals < MIN_FIRST_RENEWALS && clientRows && clientRows.length) {
    counts = transitionCounts(poolCycles(clientRows, basis));
    usedClientCurve = true;
    warnings.push(`only ${firstRenewals} first renewals (< ${MIN_FIRST_RENEWALS}); the renewal curve uses the client's pooled curve`);
  } else if (firstRenewals < MIN_FIRST_RENEWALS) {
    warnings.push(`only ${firstRenewals} first renewals (< ${MIN_FIRST_RENEWALS}); the curve is noisy`);
  }

  const curve = decomposedCurve(counts);
  const value = valuePerPayer(pooled);
  const cadenceDays = cadenceOf(rows);
  if (!curve) {
    return { ...emptyDecomposed(warnings.concat("no renewal observed yet")), cadenceDays, value };
  }
  if (!curve.sbg) warnings.push("fewer than 2 cycles from cycle 3 on: the cancel side carries its last observed rate");

  // The page's "measured" cycles are the product's own observed cycles (the
  // client pool, when used, only shapes the projection).
  const lastObserved = usedClientCurve
    ? (transitionCounts(pooled).slice(-1)[0]?.cycle || 1)
    : curve.lastObservedCycle;

  const horizons = {};
  for (const H of HORIZONS) {
    const K = horizonCycles(H, cadenceDays);
    horizons[H] = { K, ...sumValue(curve.renewal, value, K, lastObserved) };
  }

  // "Why customers leave": each cycle's eligible split four ways.
  const leave = [...pooled.values()]
    .filter((p) => p.cycle >= 2 && p.eligible > 0)
    .sort((a, b) => a.cycle - b.cycle)
    .map((p) => ({
      cycle: p.cycle,
      eligible: p.eligible,
      renewedFirstTry: p.renewedFirstTry / p.eligible,
      renewedAfterDecline: p.renewedAfterDecline / p.eligible,
      lostPaymentFailed: p.lostPaymentFailed / p.eligible,
      lostNoAttempt: p.lostNoAttempt / p.eligible,
    }));

  // Revenue lines, life to date, per acquired subscription.
  const acquired = pooled.get(1)?.cohortSize || 0;
  const tot = (f) => [...pooled.values()].reduce((s, p) => s + p[f], 0);
  const per = (x) => (acquired > 0 ? x / acquired : 0);
  const revenueLines = {
    acquired,
    subscription: per(tot("subRev")),
    refunds: -per(tot("refundAmt")),
    chargebacks: -per(tot("chargebackAmt")),
    addons: per(tot("addonRev")),
    upsells: per(tot("upsellRev")),
    otherRefundsChargebacks: -per(tot("otherRefundAmt") + tot("otherChargebackAmt")),
    net: per(tot("netValue")),
  };

  return {
    basis,
    cadenceDays,
    usedClientCurve,
    firstRenewals,
    lastObservedCycle: lastObserved,
    sbg: curve.sbg,
    curve,
    value,
    horizons,
    // Survival x value for every cycle 1..30 (cost inputs and break-even use it).
    cycles: sumValue(curve.renewal, value, MAX_CYCLE, lastObserved).survival,
    leave,
    revenueLines,
    cohorts: cohortBreakdown(rows, curve, value, cadenceDays, basis),
    warnings,
  };
}

/**
 * Contribution-margin view of a decomposed result: the user's cost and CAC
 * assumptions applied per paying subscription per cycle. Cheap to recompute,
 * so the page calls it on every input change without refitting the curve.
 * @param {object} model        computeDecomposedLtv() result
 * @param {object} assumptions  { cac, ship, proc_pct (fraction), proc_txn, cogs }
 */
export function decomposedEconomics(model, assumptions = {}) {
  const a = {
    cac: num(assumptions.cac), ship: num(assumptions.ship), proc_pct: num(assumptions.proc_pct),
    proc_txn: num(assumptions.proc_txn), cogs: num(assumptions.cogs),
  };
  const hasCosts = a.cac > 0 || a.ship > 0 || a.proc_pct > 0 || a.proc_txn > 0 || a.cogs > 0;

  let cum = 0;
  const cycles = (model.cycles || []).map((c) => {
    const econ = cycleEconomics(c.value, a);
    cum += c.survival * econ.contribution;
    return {
      cycle: c.cycle,
      observed: c.observed,
      survival: c.survival,
      value: c.value,
      varCost: econ.varCost,
      contribution: econ.contribution,
      contributionPerAcquired: c.survival * econ.contribution,
      cumContribution: cum,
    };
  });

  const horizons = {};
  for (const H of HORIZONS) {
    const K = model.horizons?.[H]?.K || 0;
    const contributionLtv = cycles.slice(0, K).reduce((s, c) => s + c.contributionPerAcquired, 0);
    horizons[H] = {
      K,
      contributionLtv,
      netOfCac: contributionLtv - a.cac,
      ltvToCac: a.cac > 0 ? contributionLtv / a.cac : null,
    };
  }

  // Break-even: interpolated cycle where cumulative contribution reaches CAC,
  // within the 30 modelled cycles.
  let breakEvenCycle = a.cac <= 0 ? 0 : null;
  if (a.cac > 0) {
    let prev = 0;
    for (const c of cycles) {
      if (c.cumContribution >= a.cac) {
        const span = c.cumContribution - prev;
        breakEvenCycle = c.cycle - 1 + (span > 0 ? (a.cac - prev) / span : 0);
        break;
      }
      prev = c.cumContribution;
    }
  }
  const cadence = model.cadenceDays || DEFAULT_CADENCE_DAYS;
  return {
    assumptions: a,
    hasCosts,
    cycles,
    horizons,
    breakEvenCycle,
    breakEvenReached: breakEvenCycle != null,
    // Cycle c ends (c - 1) cadences after the first payment.
    breakEvenMonths: breakEvenCycle != null ? (Math.max(breakEvenCycle - 1, 0) * cadence) / DAYS_PER_MONTH : null,
  };
}

function emptyDecomposed(warnings) {
  const horizons = {};
  for (const H of HORIZONS) horizons[H] = { K: 0, ltv: 0, measured: 0, projected: 0, survival: [] };
  return {
    basis: "new", cadenceDays: DEFAULT_CADENCE_DAYS, usedClientCurve: false, firstRenewals: 0,
    lastObservedCycle: 0, sbg: null, curve: null, value: [], horizons, cycles: [], leave: [],
    revenueLines: null, cohorts: [], warnings,
  };
}
